import type { ScenePreset } from "@cfg/brand-sdk";
import type { BBox, MeshData } from "@cfg/kernel";
import { Environment, GizmoHelper, GizmoViewport, Grid, Html, Lightformer, OrbitControls } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import * as THREE from "three";
import { useConfiguratorContext, useConfiguratorStore } from "../context.tsx";
import { useBusy, useDesign, useMesh, useParams } from "../hooks.ts";
import { prefersReducedMotion } from "../timing.ts";
import { FLOAT_LABEL_PRIMARY, pickFloatingLabels } from "./labels.ts";
import { Model, type ModelDisplay } from "./Model.tsx";

type OrbitControlsImpl = THREE.EventDispatcher<{ start: object; end: object }> & { target: THREE.Vector3; update(): void };

/* ------------------------------- camera fit -------------------------------- */

function bboxSphere(b: BBox): { center: THREE.Vector3; radius: number } {
  const center = new THREE.Vector3((b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2);
  const radius = Math.max(1, 0.5 * Math.hypot(b.max[0] - b.min[0], b.max[1] - b.min[1], b.max[2] - b.min[2]));
  return { center, radius };
}

/** Time constants for the exponential camera ease (≈95 % after 3τ): target ~250 ms, distance ~400 ms. */
const TARGET_TAU = 0.083;
const DIST_TAU = 0.133;
/** Re-frame the distance only when the part's size leaves this band of the last framing. */
const FRAME_BAND: [number, number] = [0.85, 1.15];

/**
 * Keeps the part framed without jumping: on a new design it snaps to the preset view; on
 * rebuilds it eases the orbit target to the new bbox centre and only re-frames the distance when
 * the part grows/shrinks out of the 85–115 % band. Holds still while the user drags the canvas
 * and snaps under prefers-reduced-motion.
 */
function CameraFit({ bbox, fitKey, preset, hint }: { bbox: BBox | null; fitKey: string; preset: ScenePreset; hint?: string | [number, number, number] }) {
  const camera = useThree((s) => s.camera) as THREE.PerspectiveCamera;
  const controls = useThree((s) => s.controls) as OrbitControlsImpl | null;
  const rig = useRef({ fitKey: "", framedRadius: 0, goalTarget: new THREE.Vector3(), goalDist: null as number | null, animating: false, dragging: false });

  useEffect(() => {
    if (!controls?.addEventListener) return;
    const start = () => (rig.current.dragging = true);
    const end = () => (rig.current.dragging = false);
    controls.addEventListener("start", start);
    controls.addEventListener("end", end);
    return () => {
      controls.removeEventListener("start", start);
      controls.removeEventListener("end", end);
    };
  }, [controls]);

  useEffect(() => {
    if (!bbox || !controls) return;
    const r = rig.current;
    const { center, radius } = bboxSphere(bbox);
    const factor = preset.camera?.distanceFactor ?? 1.15;
    const dist = (radius / Math.sin(((camera.fov / 2) * Math.PI) / 180)) * factor;
    if (r.fitKey !== fitKey) {
      // New design: snap to the preset view.
      let dir: THREE.Vector3;
      if (Array.isArray(hint)) dir = new THREE.Vector3(...hint).normalize();
      else if (hint === "front") dir = new THREE.Vector3(0, -1, 0.25).normalize();
      else if (hint === "top") dir = new THREE.Vector3(0.001, -0.001, 1).normalize();
      else dir = new THREE.Vector3(1, -1, 0.8).normalize();
      camera.position.copy(center.clone().add(dir.multiplyScalar(dist)));
      controls.target.copy(center);
      r.fitKey = fitKey;
      r.framedRadius = radius;
      r.goalDist = null;
      r.animating = false;
    } else {
      r.goalTarget.copy(center);
      const ratio = radius / (r.framedRadius || radius);
      if (ratio < FRAME_BAND[0] || ratio > FRAME_BAND[1]) {
        r.goalDist = dist;
        r.framedRadius = radius;
      }
      if (prefersReducedMotion()) {
        const delta = center.clone().sub(controls.target);
        controls.target.add(delta);
        camera.position.add(delta);
        if (r.goalDist !== null) {
          camera.position.copy(controls.target.clone().add(camera.position.clone().sub(controls.target).setLength(r.goalDist)));
          r.goalDist = null;
        }
        r.animating = false;
      } else r.animating = true;
    }
    const far = Math.max(dist, camera.position.distanceTo(controls.target));
    camera.near = Math.max(0.1, Math.min(dist, far) / 100);
    camera.far = far * 20;
    camera.updateProjectionMatrix();
    controls.update();
  }, [bbox, fitKey, camera, controls, preset, hint]);

  useFrame((_, rawDt) => {
    const r = rig.current;
    if (!r.animating || !controls || r.dragging) return;
    const dt = Math.min(rawDt, 0.1);
    const delta = r.goalTarget.clone().sub(controls.target);
    const a = 1 - Math.exp(-dt / TARGET_TAU);
    const settledTarget = delta.length() < 1e-3 * Math.max(1, r.framedRadius);
    const step = settledTarget ? delta : delta.multiplyScalar(a);
    controls.target.add(step);
    camera.position.add(step);
    if (r.goalDist !== null) {
      const off = camera.position.clone().sub(controls.target);
      const d = off.length();
      const b = 1 - Math.exp(-dt / DIST_TAU);
      const done = Math.abs(r.goalDist - d) < 2e-3 * r.goalDist;
      off.setLength(done ? r.goalDist : d + (r.goalDist - d) * b);
      camera.position.copy(controls.target).add(off);
      if (done) r.goalDist = null;
    }
    if (settledTarget && r.goalDist === null) r.animating = false;
    controls.update();
  });
  return null;
}

/* ------------------------------ scene pieces -------------------------------- */

function Lights({ preset }: { preset: ScenePreset }) {
  const l = preset.lights;
  return (
    <>
      {l.ambient !== undefined && <ambientLight intensity={l.ambient} />}
      {l.hemisphere && <hemisphereLight args={[l.hemisphere.sky, l.hemisphere.ground, l.hemisphere.intensity]} />}
      {l.key && <directionalLight position={l.key.position} intensity={l.key.intensity} color={l.key.color} castShadow={l.key.castShadow} shadow-mapSize={[2048, 2048]} />}
      {l.fill && <directionalLight position={l.fill.position} intensity={l.fill.intensity} color={l.fill.color} />}
      {l.rim && <directionalLight position={l.rim.position} intensity={l.rim.intensity} color={l.rim.color} />}
    </>
  );
}

function SceneEnvironment({ preset }: { preset: ScenePreset }) {
  const env = preset.environment;
  if (!env || (!env.hdri && !env.preset && !env.lightformers?.length)) return null;
  return (
    <Environment
      files={env.hdri}
      preset={env.hdri ? undefined : env.preset}
      background={env.background}
      environmentIntensity={env.intensity ?? 1}
      resolution={256}
    >
      {env.lightformers?.map((l, i) => (
        <Lightformer
          key={i}
          form={l.form ?? "rect"}
          intensity={l.intensity}
          color={l.color}
          position={l.position}
          scale={typeof l.scale === "number" ? l.scale : [l.scale[0], l.scale[1], 1]}
          target={l.target ?? [0, 0, 0]}
        />
      ))}
    </Environment>
  );
}

function Stage({ preset, bbox }: { preset: ScenePreset; bbox: BBox | null }) {
  const g = preset.grid;
  if (!g?.show) return null;
  const z = bbox ? bbox.min[2] - 0.01 : 0;
  const extent = bbox ? Math.max(200, 4 * Math.max(bbox.max[0] - bbox.min[0], bbox.max[1] - bbox.min[1])) : 400;
  return (
    <Grid
      position={[0, 0, z]}
      rotation={[Math.PI / 2, 0, 0]}
      args={[extent, extent]}
      cellSize={g.cellSize ?? 8}
      sectionSize={g.sectionSize ?? 40}
      cellColor={g.color ?? "#c9d1dc"}
      sectionColor={g.sectionColor ?? "#9fb0c4"}
      fadeDistance={g.fadeDistance ?? extent}
      infiniteGrid={g.infinite ?? true}
      side={THREE.DoubleSide}
    />
  );
}

/* ---------------------------- floating labels ------------------------------- */

interface Anchored {
  name: string;
  label: string;
  anchor: [number, number, number];
  value: unknown;
  unit?: string;
}

/** Hide lower-priority labels that overlap a shown one (rects from the DOM, checked ~10×/s). */
function useLabelCollisions(refs: React.MutableRefObject<(HTMLElement | null)[]>, count: number) {
  const last = useRef(0);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (t - last.current < 0.1) return;
    last.current = t;
    const shown: DOMRect[] = [];
    for (let i = 0; i < count; i++) {
      const el = refs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const pad = 4;
      const hit = shown.some((o) => r.left < o.right + pad && r.right > o.left - pad && r.top < o.bottom + pad && r.bottom > o.top - pad);
      if (hit !== el.hasAttribute("data-hidden")) el.toggleAttribute("data-hidden", hit);
      el.style.visibility = hit ? "hidden" : "";
      if (!hit) shown.push(r);
    }
  });
}

function FloatingLabels({ bbox, items, active, dragging, onPick }: { bbox: BBox; items: Anchored[]; active: string | null; dragging: string | null; onPick: (name: string) => void }) {
  const refs = useRef<(HTMLElement | null)[]>([]);
  useLabelCollisions(refs, items.length);
  return (
    <>
      {items.map((it, i) => {
        const p: [number, number, number] = [
          bbox.min[0] + (bbox.max[0] - bbox.min[0]) * it.anchor[0],
          bbox.min[1] + (bbox.max[1] - bbox.min[1]) * it.anchor[1],
          bbox.min[2] + (bbox.max[2] - bbox.min[2]) * it.anchor[2],
        ];
        return (
          <Html key={it.name} position={p} center zIndexRange={[10 + items.length - i, 0]} style={{ pointerEvents: "auto" }}>
            <button
              type="button"
              ref={(el) => {
                refs.current[i] = el;
              }}
              className={`cfg-float${active === it.name ? " is-active" : ""}${dragging === it.name ? " is-dragging" : ""}`}
              data-param={it.name}
              tabIndex={-1}
              onClick={() => onPick(it.name)}
              title={it.label}
            >
              <span className="cfg-float-label">{it.label}</span>
              <span className="cfg-float-value">
                {typeof it.value === "number" ? Number(it.value.toFixed(2)) : String(it.value)}
                {it.unit ? ` ${it.unit}` : ""}
              </span>
            </button>
          </Html>
        );
      })}
    </>
  );
}

/* --------------------------------- viewport --------------------------------- */

export interface ViewportProps {
  preset: ScenePreset;
  /** Brand model colour (CSS colour). */
  modelColor: string;
  showLabels?: boolean;
  /** Called when a floating label is clicked; the default scrolls to the field. */
  onPickParam?: (name: string) => void;
  className?: string;
  children?: ReactNode;
  /** Render a mesh other than the store's (catalog thumbnails). */
  mesh?: MeshData | null;
  /** Wireframe / feature-edge overlays (design studio). */
  display?: ModelDisplay;
  /** Overrides the preset: `true` spins at the preset speed (or 2), a number sets the speed, `false` stops it. */
  autoRotate?: boolean | number;
  /** XYZ axis gizmo in the bottom-right corner; click an axis to look along it. */
  gizmo?: boolean;
  /** Accessible name of the viewport region. Default "3D preview of <design title>". */
  label?: string;
  /** Text of the delayed busy badge. Default "building…". */
  busyLabel?: string;
}

/**
 * The 3D stage. Busy state is delayed (`useBusy`): `.is-building` / `data-busy` and the
 * `.cfg-viewport-badge` appear only after 250 ms of continuous building and stay ≥ 400 ms.
 */
export function Viewport({ preset, modelColor, showLabels = true, onPickParam, className, children, mesh: meshOverride, display, autoRotate, gizmo = false, label, busyLabel = "building…" }: ViewportProps) {
  const { mesh: storeMesh } = useMesh();
  const { showBusy } = useBusy();
  const { description } = useDesign();
  const { params, setActive } = useParams();
  const { store } = useConfiguratorContext();
  const active = useParamsActive();
  const dragging = useConfiguratorStore((s) => s.dragParam);
  const mesh = meshOverride ?? storeMesh;
  const bbox = mesh?.bbox ?? null;
  const material = useMemo(() => preset.material({ three: THREE, color: modelColor, lod: "preview" }), [preset, modelColor]);
  useEffect(() => () => material.dispose(), [material]);
  const fitKey = description ? `${description.id}@${description.version}` : "none";
  const background = preset.background;
  const bgStyle =
    typeof background === "string"
      ? { background }
      : { background: `linear-gradient(180deg, ${background.gradient[0]}, ${background.gradient[1]})` };

  const labels: Anchored[] = useMemo(() => {
    if (!showLabels || !description) return [];
    const spec = description.spec as { name: string; label: string; type: string; anchor?: [number, number, number]; unit?: string }[];
    return pickFloatingLabels(spec, active, FLOAT_LABEL_PRIMARY, description.ui?.order as string[] | undefined).map((s) => ({
      name: s.name,
      label: s.label,
      anchor: s.anchor!,
      value: params[s.name],
      unit: s.unit,
    }));
  }, [description, params, showLabels, active]);

  const pick = (name: string) => {
    setActive(name);
    if (onPickParam) return onPickParam(name);
    store.getState().requestFocus(name);
  };
  const title = description ? String(description.meta?.title ?? description.id) : "";

  const presetSpin = preset.camera?.autoRotate ?? 0;
  const spin = autoRotate === undefined ? presetSpin : autoRotate === true ? presetSpin || 2 : autoRotate || 0;

  const tone = preset.toneMapping === "None" ? THREE.NoToneMapping : preset.toneMapping === "Neutral" ? THREE.NeutralToneMapping : THREE.ACESFilmicToneMapping;

  return (
    <div
      className={`cfg-viewport${showBusy ? " is-building" : ""}${className ? ` ${className}` : ""}`}
      style={bgStyle}
      role="group"
      aria-roledescription="3D viewport"
      aria-label={label ?? (title ? `3D preview of ${title}` : "3D preview")}
      data-busy={showBusy || undefined}
    >
      <Canvas
        shadows={preset.shadows ? "percentage" : false}
        camera={{ fov: preset.camera?.fov ?? 35, up: [0, 0, 1], position: [120, -120, 90], near: 0.5, far: 5000 }}
        gl={{ antialias: true, toneMapping: tone, toneMappingExposure: preset.exposure ?? 1, alpha: true }}
        dpr={[1, 2]}
      >
        <Lights preset={preset} />
        <SceneEnvironment preset={preset} />
        <Stage preset={preset} bbox={bbox} />
        <Model mesh={mesh} material={material} display={display} />
        {bbox && labels.length > 0 && <FloatingLabels bbox={bbox} items={labels} active={active} dragging={dragging} onPick={pick} />}
        <CameraFit bbox={bbox} fitKey={fitKey} preset={preset} hint={description?.ui?.camera as string | [number, number, number] | undefined} />
        <OrbitControls makeDefault enableDamping dampingFactor={0.1} autoRotate={spin !== 0} autoRotateSpeed={spin} />
        {gizmo && (
          <GizmoHelper alignment="bottom-right" margin={[64, 64]}>
            <GizmoViewport axisColors={["#e5484d", "#30a46c", "#3e63dd"]} labelColor="#ffffff" />
          </GizmoHelper>
        )}
        {preset.effects ? <preset.effects /> : null}
        {children}
      </Canvas>
      {showBusy && (
        <div className="cfg-viewport-badge" aria-hidden>
          {busyLabel}
        </div>
      )}
    </div>
  );
}

function useParamsActive(): string | null {
  return useConfiguratorStore((s) => s.activeParam);
}
